import { Mark, mergeAttributes } from '@tiptap/core';

export interface AnnotationOptions {
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        annotation: {
            /**
             * Set an annotation mark
             */
            setAnnotation: (options: { text: string; elaborated: string }) => ReturnType;
            /**
             * Toggle an annotation mark
             */
            toggleAnnotation: (options: { text: string; elaborated: string }) => ReturnType;
            /**
             * Unset an annotation mark
             */
            unsetAnnotation: () => ReturnType;
        };
    }
}

export const Annotation = Mark.create<AnnotationOptions>({
    name: 'annotation',

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    priority: 1000,

    inclusive: false,

    addAttributes() {
        return {
            text: {
                default: '',
                parseHTML: element => element.textContent || '',
                renderHTML: attributes => {
                    // text is rendered as the content, not an attribute
                    return {};
                },
            },
            elaborated: {
                default: '',
                parseHTML: element => element.getAttribute('data-annotation') || '',
                renderHTML: attributes => {
                    if (!attributes.elaborated) {
                        return {};
                    }
                    return {
                        'data-annotation': attributes.elaborated,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span.annotation-tooltip',
                getAttrs: element => {
                    const elaborated = (element as HTMLElement).getAttribute('data-annotation') || '';
                    const text = (element as HTMLElement).textContent || '';
                    return {
                        text,
                        elaborated,
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes, mark }) {
        return [
            'span',
            mergeAttributes(
                {
                    class: 'annotation-tooltip',
                },
                this.options.HTMLAttributes,
                HTMLAttributes,
                {
                    'data-annotation': mark.attrs.elaborated || '',
                }
            ),
            0, // 0 means render the content inside
        ];
    },

    addCommands() {
        return {
            setAnnotation:
                options =>
                    ({ commands }) => {
                        return commands.setMark(this.name, {
                            text: options.text,
                            elaborated: options.elaborated,
                        });
                    },
            toggleAnnotation:
                options =>
                    ({ commands }) => {
                        return commands.toggleMark(this.name, {
                            text: options.text,
                            elaborated: options.elaborated,
                        });
                    },
            unsetAnnotation:
                () =>
                    ({ commands }) => {
                        return commands.unsetMark(this.name);
                    },
        };
    },

    // Exclude from code blocks and code marks
    excludes: 'code',
});

export default Annotation;

